import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Bell, CalendarDays, FileText, Home, ListChecks, LogOut, Search } from 'lucide-react';
import { sessionRole, safeNotificationRoute } from './routes';
import { clearPortalSession } from './accountSession';
import { initializePushListeners, refreshPushRegistration } from './push';
import { mobileRequest } from './api';
import './mobile.css';

export function MobileLanding() {
  const role = sessionRole();
  if (role) return <Navigate to={role === 'firm' ? '/dashboard' : '/client/dashboard'} replace />;
  return <main className="mobile-welcome"><img src="/logo.png" alt="Cisowski Law feather" /><h1>Cisowski Law</h1><p>Your case, documents, and next steps in one place.</p><Link to="/client/login" className="mobile-primary">Client portal</Link><Link to="/login-firm" className="mobile-secondary">Firm sign in</Link><p className="mobile-welcome-caption">Use your existing account to sign in.</p></main>;
}

export default function MobileShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const role = sessionRole();
  const [online, setOnline] = useState(navigator.onLine);
  const [error, setError] = useState('');
  const [prompt, setPrompt] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  useEffect(() => {
    if (!isNative) return undefined;
    document.documentElement.classList.add('native-app');
    const onlineChanged = () => { setOnline(navigator.onLine); if (navigator.onLine) refreshPushRegistration(); };
    window.addEventListener('online', onlineChanged);
    window.addEventListener('offline', onlineChanged);
    return () => { window.removeEventListener('online', onlineChanged); window.removeEventListener('offline', onlineChanged); };
  }, [isNative]);
  useEffect(() => {
    if (!isNative || !role) return undefined;
    let dispose;
    let resume;
    let disposed = false;
    const refresh = () => window.dispatchEvent(new CustomEvent('portal-notifications-changed'));
    const route = async (value, id) => {
      // Lock-screen pushes contain only an opaque ID. Fetch details through the
      // current account before opening anything, including after account changes.
      try {
        if (!id) throw new Error('No notification identifier.');
        const item = await mobileRequest(`/notifications/${encodeURIComponent(id)}/`);
        const destination = safeNotificationRoute(item.deep_link || item.route, role);
        await mobileRequest(`/notifications/${encodeURIComponent(id)}/read/`, { method: 'POST' });
        navigate(destination || (role === 'client' ? '/client/dashboard/notifications' : '/notifications'));
        refresh();
      } catch { navigate(role === 'client' ? '/client/dashboard/notifications' : '/notifications'); }
    };
    initializePushListeners(route, refresh).then(cleanup => { if (disposed) cleanup(); else dispose = cleanup; }).catch(() => {});
    App.addListener('appStateChange', ({ isActive }) => { if (isActive) { refresh(); refreshPushRegistration(); } }).then(handle => { if (disposed) handle.remove(); else resume = handle; });
    return () => { disposed = true; dispose?.(); resume?.remove(); };
  }, [isNative, role, navigate]);
  useEffect(() => {
    if (!isNative) return undefined;
    let handle;
    App.addListener('backButton', ({ canGoBack }) => { if (canGoBack && pathname !== '/' && pathname !== '/dashboard' && pathname !== '/client/dashboard') navigate(-1); else App.minimizeApp(); }).then(value => { handle = value; });
    return () => handle?.remove();
  }, [isNative, navigate, pathname]);
  if (!isNative) return null;
  const links = role === 'firm' ? [[Home, 'Home', '/dashboard'], [Search, 'Cases', '/case-search'], [ListChecks, 'ToDos', '/todays-todos'], [CalendarDays, '341s', '/341-workboard'], [Bell, 'Inbox', '/notifications']] : [[Home, 'Home', '/client/dashboard'], [FileText, 'Documents', '/client/dashboard/documents'], [Bell, 'Inbox', '/client/dashboard/notifications']];
  return <>
    {!online && <div className="mobile-connection" role="status">You’re offline. Reconnect to load cases, upload files, and receive updates.</div>}
    {!role && pathname !== '/' && <Link to="/" className="mobile-account-choice">← Choose firm or client sign in</Link>}
    {role && <><button type="button" onClick={() => setPrompt(true)} className="mobile-switch" aria-label="Sign out or switch account"><LogOut size={17} /></button><nav className="mobile-bottom-nav" aria-label="App navigation">{links.map(([Icon, label, url]) => <Link key={url} to={url} aria-current={pathname === url ? 'page' : undefined}><Icon size={22} /><span>{label}</span></Link>)}</nav></>}
    {prompt && <div className="mobile-confirm-backdrop"><div role="dialog" aria-modal="true" aria-labelledby="mobile-switch-title" className="mobile-confirm"><h2 id="mobile-switch-title">Sign out of this account?</h2><p>You can then choose firm or client sign in.</p>{error && <p role="alert" className="text-red-700">{error}</p>}<button type="button" className="mobile-primary" onClick={async () => { try { await clearPortalSession(); setPrompt(false); setError(''); navigate('/', { replace: true }); } catch (requestError) { setError(requestError.message); } }}>Sign out</button><button type="button" className="mobile-secondary" onClick={() => { setPrompt(false); setError(''); }}>Stay signed in</button></div></div>}
  </>;
}
